import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useCompany } from "./useCompany";
import { SUBSCRIPTION_TIERS, SubscriptionTierKey } from "@/lib/subscriptionTiers";
import { useBackendSubscription } from "./useBackendSubscription";
import { computeUsageLevel, parseServerJobUsage, type ServerJobUsage, type UsageWarningLevel } from "@/lib/jobLimit";

export type { UsageWarningLevel } from "@/lib/jobLimit";

interface JobUsageData {
  /** Billable jobs used in the current billing period (name kept for existing callers). */
  currentMonthJobs: number;
  jobLimit: number | null;
  jobsRemaining: number | null;
  tier: SubscriptionTierKey;
  planName: string;
  subscriptionStatus: string | null;
  periodStart: string | null;
  periodEnd: string | null;
  /** "server" = authoritative get_my_job_usage(); "legacy" = old monthly counter fallback. */
  source: "server" | "legacy";
  isAtLimit: boolean;
  isNearLimit: boolean;
  /** 95%+ of job limit */
  isNear95: boolean;
  percentageUsed: number;
  /** Computed warning level: none | soft (80%) | strong (95%) | critical (100%) */
  warningLevel: UsageWarningLevel;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

const isTierKey = (t: string | null | undefined): t is SubscriptionTierKey =>
  !!t && Object.prototype.hasOwnProperty.call(SUBSCRIPTION_TIERS, t);

export function useJobUsage(): JobUsageData {
  const { companyId } = useCompany();
  const backendSub = useBackendSubscription();
  const [server, setServer] = useState<ServerJobUsage | null>(null);
  const [legacyCount, setLegacyCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchJobCount = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Authoritative usage (company derived server-side from the session).
      const { data: rpcData, error: rpcError } = await (supabase.rpc as any)("get_my_job_usage");
      const parsed = rpcError ? null : parseServerJobUsage(rpcData);
      if (parsed) {
        setServer(parsed);
        return;
      }
      setServer(null);

      // Fallback until the server function exists: legacy calendar-month counter.
      const monthYear = new Date().toISOString().slice(0, 7);
      const { data: usageData, error: usageError } = await supabase
        .from("company_job_usage")
        .select("job_count")
        .eq("company_id", companyId)
        .eq("month_year", monthYear)
        .maybeSingle();

      if (usageError) throw usageError;
      setLegacyCount(usageData?.job_count || 0);
    } catch (err: any) {
      console.error("Error fetching job usage:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  // Refetch when the confirmed plan changes (e.g. webhook-confirmed upgrade).
  useEffect(() => {
    fetchJobCount();
  }, [fetchJobCount, backendSub.tier, backendSub.status]);

  // Refetch when the app regains focus so other devices/users are reflected.
  useEffect(() => {
    const onFocus = () => { if (document.visibilityState === "visible") fetchJobCount(); };
    document.addEventListener("visibilitychange", onFocus);
    return () => document.removeEventListener("visibilitychange", onFocus);
  }, [fetchJobCount]);

  let tier: SubscriptionTierKey;
  let jobLimit: number | null;
  let used: number;
  let threshold: number | undefined;
  if (server) {
    tier = isTierKey(server.tier) ? server.tier : "starter";
    jobLimit = server.unlimited || server.is_demo ? null : server.job_limit;
    used = server.used;
    threshold = server.warning_threshold;
  } else {
    tier = backendSub.tier || "starter";
    jobLimit = backendSub.tier ? backendSub.jobLimit : SUBSCRIPTION_TIERS.starter.jobLimit;
    used = legacyCount;
  }

  const level = computeUsageLevel(used, jobLimit, threshold);

  return {
    currentMonthJobs: used,
    jobLimit,
    jobsRemaining: level.remaining,
    tier,
    planName: server?.plan || SUBSCRIPTION_TIERS[tier].name,
    subscriptionStatus: backendSub.status,
    periodStart: server?.period_start ?? null,
    periodEnd: server?.period_end ?? null,
    source: server ? "server" : "legacy",
    isAtLimit: level.isAtLimit,
    isNearLimit: level.isNearLimit,
    isNear95: level.isNear95,
    percentageUsed: level.percentageUsed,
    warningLevel: level.warningLevel,
    loading: loading || backendSub.loading,
    error: error || backendSub.error,
    refresh: async () => {
      await Promise.all([fetchJobCount(), backendSub.refresh()]);
    },
  };
}
